import {Inject, Injectable} from '@angular/core';
import {Observable, ReplaySubject, Subject, throwError} from 'rxjs';
import {Partner} from '../interface/partner';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {catchError, first, take} from 'rxjs/operators';
import {CountedAndPaginated} from '../interface/response/counted-and-paginated';
import {FilterService} from './filter.service';
import {DOCUMENT} from '@angular/common';
import {ListResponse} from '../interface/response/list-response';
import {Ofwca} from '../interface/ofwca';
import {Contract} from '../interface/contract';
import {Document as DocumentInterface} from '../interface/document';
import {PartnerFacility} from '../interface/partner-facility';
import {Uuid} from '../interface/response/uuid';
import {Submissions} from "../interface/submissions";
import {PartnerGus} from "../interface/partner-gus";
import {OfwcaRegistrationInfo} from "../interface/ofwca-registration-info";
import {Department} from "../interface/department";
import {ReportCustom} from "../interface/report-custom";
import {Note as NoteInterface} from "../interface/note";
import {CommissionSettlement} from "../interface/commission-settlement";
import {AffiliateCode} from "../interface/affiliateCode";

@Injectable({
  providedIn: 'root'
})
export class PartnerService {
  private readonly partnerListSubject: ReplaySubject<CountedAndPaginated<Partner>> = new ReplaySubject<CountedAndPaginated<Partner>>(1);
  private readonly partnerNotCountedListSubject: ReplaySubject<ListResponse<Partner>> = new ReplaySubject<ListResponse<Partner>>(1);
  public readonly partnerGusListSubject: ReplaySubject<CountedAndPaginated<PartnerGus>>
    = new ReplaySubject<CountedAndPaginated<PartnerGus>>(1);
  private readonly partnerSearchEngineListSubject: ReplaySubject<CountedAndPaginated<Partner>>
    = new ReplaySubject<CountedAndPaginated<Partner>>(1);

  constructor(
    private httpClient: HttpClient,
    private filterService: FilterService,
    @Inject(DOCUMENT) private document: Document,
  ) {
  }

  public loadList(page: number, pageSize: number, filters: object,
                  sort?: { sortBy?: string, sortOrder?: string }): void {
    this.httpClient.get(`/rest/crm/partner?page=${page + 1}&pageSize=${pageSize}&sortBy=${sort.sortBy ?? ''}&sortOrder=${sort.sortOrder ?? ''}&` + this.filterService.parseFilters(filters))
      .pipe(take(1))
      .subscribe((result: CountedAndPaginated<Partner>) => {
        this.partnerListSubject.next(result);
      });
  }

  public loadNotCountedList(page: number, pageSize: number, filters: object, observable?: Subject<ListResponse<Partner>>): void {
    this.httpClient
      .get(`/rest/crm/partner?disableCount=1&page=${page + 1}&pageSize=${pageSize}&` + this.filterService.parseFilters(filters))
      .pipe(take(1))
      .subscribe((result: ListResponse<Partner>) => {
        if (observable) {
          observable.next(result);
        } else {
          this.partnerNotCountedListSubject.next(result);
        }
      });
  }

  public getPartnerListSubject(): ReplaySubject<CountedAndPaginated<Partner>> {
    return this.partnerListSubject;
  }

  public getPartnerNotCountedListSubject(): ReplaySubject<ListResponse<Partner>> {
    return this.partnerNotCountedListSubject;
  }

  public downloadReport(filters: object): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/partner/method/report?` + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }

  public downloadReportAffiliateCode(filters: object): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/affiliate-code/method/report?` + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }

  public downloadCustomReport(filters: object): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/partner/custom/method/report?` + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }

  public getCustomReportSettings(reportName: string): Observable<ReportCustom> {
    return this.httpClient.get(`/rest/crm/report/custom/settings/${reportName}`)
      .pipe(first()) as Observable<ReportCustom>
      ;
  }

  public sendCustomReportSettings(formData: object): Observable<any> {
    return this.httpClient.post('/rest/crm/report/custom/settings', formData)
      .pipe(take(1));
  }

  public sendForm(formData: object, partnerId?: string): Observable<any> {
    if (!partnerId) {
      return this.httpClient.post('/rest/crm/partner', formData)
        .pipe(take(1))
        ;
    } else {
      return this.httpClient.put(`/rest/crm/partner/${partnerId}`, formData)
        .pipe(take(1))
        ;
    }
  }

  public get(uuid: string): Observable<any> {
    return this.httpClient.get(`/rest/crm/partner/${uuid}`)
      .pipe(take(1))
      ;
  }

  public getPartnerOfwcas(uuid: string): Observable<ListResponse<Ofwca>> {
    return this.httpClient.get(`/rest/crm/partner/${uuid}/ofwca`)
      .pipe(first()) as Observable<ListResponse<Ofwca>>;
  }

  public getPartnerContracts(uuid: string): Observable<ListResponse<Contract>> {
    return this.httpClient.get(`/rest/crm/partner/${uuid}/contract`)
      .pipe(first()) as Observable<ListResponse<Contract>>;
  }

  public getAffiliateCode(uuid: string): Observable<ListResponse<AffiliateCode>> {
    return this.httpClient.get(`/rest/crm/partner/${uuid}/affiliateCode`)
      .pipe(first()) as Observable<ListResponse<AffiliateCode>>;
  }


  public getPartnerDocuments(uuid: string): Observable<ListResponse<DocumentInterface>> {
    return this.httpClient.get(`/rest/crm/partner/${uuid}/document`)
      .pipe(first()) as Observable<ListResponse<DocumentInterface>>;
  }

  public getPartnerFacilities(uuid: string): Observable<ListResponse<PartnerFacility>> {
    return this.httpClient.get(`/rest/crm/partner/${uuid}/facility`)
      .pipe(first()) as Observable<ListResponse<PartnerFacility>>;
  }

  public getPartnerNotes(uuid: string): Observable<ListResponse<NoteInterface>> {
    return this.httpClient.get(`/rest/crm/partner/${uuid}/note`)
      .pipe(first()) as Observable<ListResponse<NoteInterface>>;
  }

  public deletePartner(uuid: string): Observable<any> {
    return this.httpClient.delete(`/rest/crm/partner/${uuid}`)
      .pipe(first());
  }

  public changeRegional(formData: any, uuid: string): Observable<any> {
    return this.httpClient.post(`/rest/crm/partner/${uuid}/changeRegional`, formData)
      .pipe(first());
  }

  public changeAccountNumber(formData: any, uuid: string): Observable<any> {
    return this.httpClient.post(`/rest/crm/partner/${uuid}/changeAccountNumber`, formData)
      .pipe(first());
  }

  public getChildPartners(uuid: string): Observable<ListResponse<Partner>> {
    return this.httpClient.get(`/rest/crm/partner/${uuid}/partner`)
      .pipe(first()) as Observable<ListResponse<Partner>>;
  }

  public getChildOvercommissionPartners(uuid: string): Observable<ListResponse<Partner>> {
    return this.httpClient.get(`/rest/crm/partner/${uuid}/partner/overcommission`)
      .pipe(first()) as Observable<ListResponse<Partner>>;
  }

  public updateCurrentDynamics(formData: any, uuid: string): Observable<any> {
    return this.httpClient.post(`/rest/crm/partner/${uuid}/current-dynamics`, formData)
      .pipe(first());
  }

  public gusInformation(nip: string): Observable<PartnerGus> {
    return this.httpClient.get(`/rest/crm/partner/gus/${nip}`)
      .pipe(first()) as Observable<PartnerGus>;
  }

  public getPartnerOfwca(partnerId: string): Observable<ListResponse<Ofwca>> {
    return this.httpClient.get(`/rest/crm/partner/${partnerId}/ofwcaList`)
      .pipe(first()) as Observable<ListResponse<Ofwca>>;
  }

  public searchEngine(page: number, pageSize: number, filters: object): void {
    this.httpClient.get(`/rest/crm/partner-search-engine?page=${page + 1}&pageSize=${pageSize}&` + this.filterService.parseFilters(filters))
      .pipe(take(1))
      .subscribe((result: CountedAndPaginated<Partner>) => {
        this.partnerSearchEngineListSubject.next(result);
      });
  }

  public getPartnerSearchEngineListSubject(): ReplaySubject<CountedAndPaginated<Partner>> {
    return this.partnerSearchEngineListSubject;
  }

  public sendFormOck(formData: object): Observable<any> {
    return this.httpClient.post('/rest/crm/partner/ock', formData).pipe(
      take(1)
    );
  }

  public editOCKAccount(uuid: string): Observable<any> {
    return this.httpClient.patch(`/rest/crm/partner-ock-account/${uuid}`, {}).pipe(
      take(1)
    );
  }

  public sendPassword(uuid: string): Observable<any> {
    return this.httpClient.get( `/rest/crm/partner/${uuid}/send-password`)
      .pipe(first()) as Observable<any>;
  }

  public sendInvitation(uuid: string): Observable<any> {
    return this.httpClient.get( `/rest/crm/partner/${uuid}/send-invitation`)
      .pipe(first()) as Observable<any>;
  }


  public downloadCustomReportRegional(filters: object): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/partner/regional/custom/method/report?` + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }

  public downloadReportChildPartner(filters: object): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/partner/child-partner/method/report?` + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }

}
